import { Controller } from '@hotwired/stimulus'
import L from 'leaflet'
import 'leaflet-draw'

// Fix Leaflet default icon paths
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
})

export default class extends Controller {
  static targets = ['map', 'coordinates', 'color']
  static values = {
    coordinates: { type: Array, default: [] },
    color: { type: String, default: '#3388ff' },
    center: { type: Array, default: [43.17, 44.82] },
    zoom: { type: Number, default: 10 },
    existingZones: { type: Array, default: [] }
  }

  connect () {
    this.initMap()
    this.loadExistingZones()
    this.initDrawControls()
    this.loadExistingPolygon()
  }

  disconnect () {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initMap () {
    this.map = L.map(this.mapTarget).setView(this.centerValue, this.zoomValue)

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19
    }).addTo(this.map)

    this.drawnItems = new L.FeatureGroup()
    this.map.addLayer(this.drawnItems)
  }

  initDrawControls () {
    const drawControl = new L.Control.Draw({
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: {
            color: this.colorValue
          }
        },
        polyline: false,
        rectangle: false,
        circle: false,
        circlemarker: false,
        marker: false
      },
      edit: {
        featureGroup: this.drawnItems,
        remove: true
      }
    })
    this.map.addControl(drawControl)

    this.map.on(L.Draw.Event.CREATED, (e) => {
      this.drawnItems.clearLayers()
      this.drawnItems.addLayer(e.layer)
      this.updateCoordinates()
    })

    this.map.on(L.Draw.Event.EDITED, () => {
      this.updateCoordinates()
    })

    this.map.on(L.Draw.Event.DELETED, () => {
      this.updateCoordinates()
    })
  }

  loadExistingZones () {
    this.existingZonesValue.forEach(zone => {
      if (zone.coordinates && zone.coordinates.length >= 3) {
        const polygon = L.polygon(zone.coordinates, {
          color: zone.color || '#999',
          fillColor: zone.color || '#999',
          fillOpacity: 0.15,
          weight: 1,
          dashArray: '5, 5',
          interactive: false
        })
        polygon.bindTooltip(zone.name, { permanent: false, direction: 'center' })
        polygon.addTo(this.map)
      }
    })
  }

  loadExistingPolygon () {
    if (this.coordinatesValue.length >= 3) {
      const polygon = L.polygon(this.coordinatesValue, {
        color: this.colorValue
      })
      this.drawnItems.addLayer(polygon)
      this.map.fitBounds(polygon.getBounds(), { padding: [50, 50] })
    }
  }

  updateCoordinates () {
    const layers = this.drawnItems.getLayers()
    if (layers.length > 0) {
      const latlngs = layers[0].getLatLngs()[0]
      const coords = latlngs.map(ll => [ll.lat, ll.lng])
      this.coordinatesTarget.value = JSON.stringify(coords)
    } else {
      this.coordinatesTarget.value = ''
    }
  }

  updateColor () {
    if (!this.hasColorTarget) return

    const newColor = this.colorTarget.value
    this.drawnItems.eachLayer((layer) => {
      layer.setStyle({ color: newColor })
    })
  }
}
